// React
import { useCallback } from "react";

// Redux
import { useSelector } from "react-redux";

// Third Party
import { isBoolean } from "lodash";

function useAuthorization() {
	const user = useSelector((state) => state.user.user);

	const useAuth = useCallback(
		(action) => {
			if (Array.isArray(action)) {
				for (const item of action) {
					if (user?.actions && user.actions.includes(item)) {
						return true;
					}
				}
			}
			if (action === "*") {
				return true;
			}
			if (user?.actions) {
				return user.actions.includes(action);
			}

			return false;
		},
		[user]
	);

	const isPresentAccess = useCallback(
		(action) => {
			if (isBoolean(action)) {
				return action;
			}
			const actions = user?.actions || [];
			if (Array.isArray(action)) {
				for (const item of action) {
					if (user?.actions && user.actions.includes(item)) {
						return true;
					}
				}
			}
			return action === "*" || actions.includes(action);
		},
		[user]
	);

	/**
	 * Check whether multiple actions are present.
	 *
	 * @param {Array<any>} actions           — the list of things to test
	 * @param {"all"|"atLeastOne"} [method="all"]
	 *                                        — “all” to require every action,
	 *                                           “atLeastOne” to require just one
	 * @returns {boolean}                    — true if the test passes
	 */
	const isPresentMultipleAccess = useCallback(
		(actions, method = "all") => {
			if (actions === "*") {
				return true;
			}

			if (user?.actions?.length) {
				switch (method) {
					case "all":
						return actions.every((action) => user.actions.includes(action));
					case "atLeastOne":
						return actions.some((action) => user.actions.includes(action));
					default:
						break;
				}
			}
			return false;
		},
		[user]
	);

	const useMultipleAuth = useCallback(
		(actions, method = "all") => {
			if (actions === "*") {
				return true;
			}
			if (user?.actions?.length) {
				let accessAmount = 0;
				actions?.forEach((action) => {
					if (user.actions.includes(action)) {
						accessAmount++;
					}
				});

				if (method === "all") {
					return accessAmount === actions?.length;
				}
				if (method === "atLeastOne") {
					return Boolean(accessAmount);
				}
			}

			return false;
		},
		[user]
	);

	const usePresentMoreThan = useCallback(
		(actions, amount) => {
			if (user?.actions?.length) {
				let totalAmount = 0;
				actions?.forEach((action) => {
					if (user.actions.includes(action)) {
						totalAmount++;
					}
				});

				return totalAmount >= amount;
			}

			return false;
		},
		[user]
	);

	const usePresentByPattern = useCallback(
		(pattern, amount = 1) => {
			if (user?.actions?.length && pattern) {
				let totalAmount = 0;
				user.actions.forEach((action) => {
					if (action.includes(pattern)) {
						totalAmount++;
					}
				});

				return totalAmount >= amount;
			}

			return false;
		},
		[user]
	);

	return { useAuth, isPresentAccess, isPresentMultipleAccess, useMultipleAuth, usePresentMoreThan, usePresentByPattern };
}

export { useAuthorization };
